import React, { useEffect, useState } from "react";
import { useCustomerAuth } from "../../../context/CustomerAuthContext/CustomerAuthContext";
import { orderApi } from "../../../api/order";
import { useTheme } from "../../../context/ThemeContext";
import { Link } from "react-router-dom";
import { PackageCheck, CreditCard, Truck, Clock } from "lucide-react";

/* ===== Theme helpers ===== */
const hexToRgb = (hex: string) => {
  const h = (hex || "#0f1115").replace("#", "");
  const full = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
};
const luminance = ({ r, g, b }: { r: number; g: number; b: number }) => {
  const f = (v: number) => { v /= 255; return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4); };
  return 0.2126 * f(r) + 0.7152 * f(g) + 0.0722 * f(b);
};
const useSurfaces = (theme?: any) => {
  const bg = theme?.backgroundColor || "#0f1115";
  const isDark = luminance(hexToRgb(bg)) < 0.5;
  return {
    surface1: isDark ? "rgba(255,255,255,.035)" : "rgba(0,0,0,.035)",
    surface2: isDark ? "rgba(255,255,255,.06)"  : "rgba(0,0,0,.06)",
    divider:  isDark ? "rgba(255,255,255,.10)"  : "rgba(0,0,0,.12)",
    softShadow: isDark ? "0 10px 28px rgba(0,0,0,.28)" : "0 10px 28px rgba(0,0,0,.10)",
    textPrimary: theme?.textPrimary ?? (isDark ? "#fff" : "#111827"),
    textSecondary: theme?.textSecondary ?? (isDark ? "rgba(255,255,255,.7)" : "#6b7280"),
    accent: theme?.accent ?? "#22c55e",
    warn: "#f59e0b",
    danger: "#ef4444",
  };
};
const panel = (S: ReturnType<typeof useSurfaces>): React.CSSProperties => ({
  backgroundColor: S.surface1, borderColor: S.divider, borderWidth: 1, boxShadow: S.softShadow
});

/* ===== Badges ===== */
const statusMap: Record<number, { t: string; cls: string }> = {
  0: { t: "Alındı", cls: "bg-blue-100 text-blue-800" },
  6: { t: "Kargoda", cls: "bg-cyan-100 text-cyan-800" },
  7: { t: "Teslim", cls: "bg-emerald-100 text-emerald-800" },
  8: { t: "İptal", cls: "bg-rose-100 text-rose-800" },
  9: { t: "İade", cls: "bg-rose-100 text-rose-800" },
  4: { t: "Teslim Edilemedi", cls: "bg-amber-100 text-amber-900" },
  18:{ t: "Teslim Edilemedi", cls: "bg-amber-100 text-amber-900" },
};
const payMap: Record<number, { t: string; cls: string }> = {
  0: { t: "Ödenmedi", cls: "bg-gray-100 text-gray-800" },
  1: { t: "Ödendi", cls: "bg-emerald-100 text-emerald-800" },
  2: { t: "Kısmi", cls: "bg-amber-100 text-amber-900" },
  3: { t: "İade", cls: "bg-rose-100 text-rose-800" },
};
const Badge: React.FC<{kind:"status"|"pay"; code:number}> = ({kind, code}) => {
  const m = (kind==="status"? statusMap: payMap)[code] ?? { t:`Kod ${code}`, cls:"bg-gray-100 text-gray-800" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${m.cls}`}>{m.t}</span>;
};

const OrdersPage: React.FC = () => {
  const { customer } = useCustomerAuth();
  const { theme } = useTheme();
  const S = useSurfaces(theme);

  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      if (!customer?.id) return;
      setLoading(true);
      const res = await orderApi.list({ musteriId: customer.id, page: 1, pageSize: 50, sort: "yeni" });
      setLoading(false);
      setRows(res.success && res.data ? res.data.items : []);
    })();
  }, [customer?.id]);

  const fmt = (v: number, ccy = "TRY") =>
    new Intl.NumberFormat("tr-TR", { style: "currency", currency: ccy }).format(v || 0);

  if (loading) {
    return (
      <div className="p-6 rounded-lg border" style={panel(S)}>
        <div className="animate-pulse space-y-3">
          <div className="h-4 w-32 rounded" style={{ backgroundColor: S.surface2 }} />
          <div className="h-10 w-full rounded" style={{ backgroundColor: S.surface2 }} />
          <div className="h-10 w-2/3 rounded" style={{ backgroundColor: S.surface2 }} />
        </div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="p-6 rounded-lg border" style={panel(S)}>
        <span style={{ color: S.textSecondary }}>Henüz siparişiniz yok.</span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {rows.map((o) => (
        <Link
          key={o.siparisId}
          to={`/musteri/panel/siparisler/${o.siparisId}`}
          className="block rounded-lg border p-3 transition"
          style={{ ...panel(S), backgroundColor: S.surface1 }}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold truncate" style={{ color: S.textPrimary }}>
                Sipariş #{o.siparisNo}
              </div>
              <div className="text-xs" style={{ color: S.textSecondary }}>
                {new Date(o.siparisTarihi).toLocaleString("tr-TR")}
              </div>
              <div className="mt-1 flex items-center gap-2 text-xs">
                <span className="inline-flex items-center gap-1"><PackageCheck size={14}/><Badge kind="status" code={o.durumKodu} /></span>
                <span className="inline-flex items-center gap-1"><CreditCard size={14}/><Badge kind="pay" code={o.odemeDurumu} /></span>
              </div>
            </div>
            <div className="text-right">
              <div className="font-semibold" style={{ color: S.textPrimary }}>
                {fmt(o.genelToplamBrut, o.paraBirimiKodu)}
              </div>
              <div className="text-xs flex items-center gap-1 justify-end" style={{ color: S.textSecondary }}>
                <Clock size={14}/> Son güncelleme {o.guncellemeTarihi ? new Date(o.guncellemeTarihi).toLocaleDateString("tr-TR") : "-"}
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
};

export default OrdersPage;
